import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
    ArtAna,
    ArtImageObj,
    AziendaObj,
    CarrelloAppoggioObj,
    CarrelloConfirmResponse,
    CarrelloRequestDuplicate,
    CarrelloTestataAppoggioObj,
    CfCliUserObj,
    CfObj,
    GenericListImageRequest,
    ImageObjSlicer,
    ListiniGPO,
    ListiniObj,
    ReturnObj,
    TIPO_IMAGE,
    TIPO_RICHIESTA,
} from './ricercaSlice'

// -----------------------------------------------------------------------------
// ------------------------------------ ART ANA --------------------------------
// -----------------------------------------------------------------------------

export interface GenericQueryPaginationRequest {
    search?: string
    page: number
    pageSize: number

    filter: Array<string> | null

    listino: boolean
    history: boolean

    tipoRichiesta?: TIPO_RICHIESTA
}

export interface ArtAnaResponse {
    listini: ListiniGPO
    data: ArtAna[]
    message: string
    rowCount: number
}

export interface ArtAnaListiniResponse {
    data: ArtAna[]
    listini: ListiniObj
    message: string
    rowCount: number
}

// -----------------------------------------------------------------------------
// ------------------------------------ CARRELLO -------------------------------
// -----------------------------------------------------------------------------

export interface CarrelloAppoggioResponse {
    listini: ListiniGPO
    data: Array<CarrelloAppoggioObj>
    message: string
    rowCount: number
}

// -----------------------------------------------------------------------------
// ------------------------------------ IMAGES ---------------------------------
// -----------------------------------------------------------------------------

export interface GenericDeleteResponse {
    data: number
    message: string
}

export interface GenericDeleteRequest {
    id: string
    TIPO: TIPO_IMAGE
}

export interface GenericInsertResponse<T> {
    data?: T
    message?: string
}

export interface ArtImageInsertRequest {
    ART_IMAGE: Buffer
    COD_TIPO_IMAGE_ART?: string
    COD_ART?: string
    IMAGE_WIDTH?: number
    IMAGE_HEIGHT?: number
}

export interface ArtImageResponse {
    data: ArtImageObj[]
    dataImages: ImageObjSlicer[]
    message: string
    rowCount: number
}

// -----------------------------------------------------------------------------
// ------------------------------------ AUTH -----------------------------------
// -----------------------------------------------------------------------------

export interface LoginRequest {
    E_MAIL_CF: string
    PASS_WEB?: string
}

export interface LoginResponse {
    data?: {
        cf: CfObj
        AZIENDA_ID: string
        azienda?: AziendaObj
    }
    message?: string
}

export interface RegistrationRequest {
    RAG_SOC_CF: string
    INDI_CF: string
    CAP_CF: string
    COMUNE_CF: string
    PROVINCIA_CF: string
    STATO_CF: string
    P_IVA_CF: string
    COD_FISC_CF: string
    E_MAIL_CF: string
}

export interface ResetPasswordRequest {
    E_MAIL_CF: string
}
export interface ResetPasswordResponse { }

export interface RegistrationResponse {
    data?: ReturnObj
    message?: string
}

export interface UpdatePasswordRequest {
    TOKEN: string
    PASS_WEB: string
}

export interface UpdatePasswordResponse { }

// -----------------------------------------------------------------------------

export interface GenericQueryPagination {
    search?: string
    page: number
    pageSize: number

    filter: Array<string> | null

    listino: boolean
    history: boolean

    tipoRichiesta?: TIPO_RICHIESTA
}

export interface GenericResponse<T> {
    data: T
    message: string
    rowCount: number
}

export const ricercaApi = createApi({
    baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_ENDPOINT }),
    reducerPath: 'ricercaApi',
    tagTypes: ['all', 'searchArtAna', 'categorie', 'carrello', 'listini', 'images', 'ordine'],
    refetchOnFocus: false,
    refetchOnReconnect: false,
    keepUnusedDataFor: 1,
    endpoints: (build) => ({
        registrationPost: build.mutation<RegistrationResponse, RegistrationRequest>({
            query: (data: RegistrationRequest) => ({
                url: `/registration`,
                method: 'POST',
                body: data,
            }),
        }),
        resetPassword: build.mutation<ResetPasswordResponse, ResetPasswordRequest>({
            query: (data: RegistrationRequest) => ({
                url: `/resetPassword`,
                method: 'POST',
                body: data,
            }),
        }),
        updatePassworPost: build.mutation<UpdatePasswordResponse, UpdatePasswordRequest>({
            query: (data: UpdatePasswordRequest) => ({
                url: `/updatePassword`,
                method: 'POST',
                body: data,
            }),
        }),
        loginPost: build.mutation<LoginResponse, LoginRequest>({
            query: (data: LoginRequest) => ({
                url: `/login`,
                method: 'POST',
                body: data,
            }),
        }),
        logoutPost: build.mutation<LoginResponse, LoginRequest>({
            query: (data: LoginRequest) => ({
                url: `/logout`,
                method: 'POST',
                body: data,
            }),
        }),
        // ART ANA
        getRicercaArtAna: build.query<ArtAnaResponse, GenericQueryPagination>({
            providesTags: ['searchArtAna'],
            query: (qp) => {
                const filter = qp.filter != null ? qp.filter.join(',') : ''
                return {
                    url: `artAna?search=${qp.search}&page=${qp.page}&pageSize=${qp.pageSize}&history=${qp.history}&listino=${qp.listino}&filter=${filter}`,
                    method: 'GET',
                }
            },
            transformResponse: (response: ArtAnaResponse, meta, args) => {
                return response
            },
        }),
        // CATEGORIE MERCEOLOGICHE
        getCategorie: build.query<GenericResponse<any>, void>({
            providesTags: ['categorie'],
            query: (queryPagination) => ({
                url: `artAna/categorie`,
                method: 'GET',
            }),
            transformResponse: (response: GenericResponse<any>, meta, args) => {
                return response
            },
        }),
        // ART LIST PREZZI
        getListiniOfferte: build.query<ArtAnaResponse, GenericQueryPaginationRequest>({
            providesTags: ['listini'],
            query: (qp) => ({
                url: `listiniOfferte?search=${qp.search}&page=${qp.page}&pageSize=${qp.pageSize}`,
                method: 'GET',
            }),
            transformResponse: (response: ArtAnaResponse, meta, args) => {
                return response
            }
        }),
        // --------------------------------------------------------------------------------------------
        // ------------------------------------------- LIST CF ----------------------------------------
        // --------------------------------------------------------------------------------------------
        getCf: build.query<GenericResponse<CfCliUserObj>, void>({
            query: (data) => ({
                url: `cf`,
                method: 'GET',
            }),
            keepUnusedDataFor: 10,
            forceRefetch({ endpointState }) {
                const lastFetched = endpointState?.fulfilledTimeStamp;
                if (!lastFetched) return true;
                return (Date.now() - lastFetched) > 10000;
            },
        }),
        // --------------------------------------------------------------------------------------------
        // ------------------------------------------- LIST CARRELLO ----------------------------------
        // --------------------------------------------------------------------------------------------
        getCarrelloAppoggio: build.query<CarrelloAppoggioResponse, void>({
            providesTags: ['carrello'],
            query: (data) => ({
                url: `carrelloAppoggio`,
                method: 'GET',
            }),
        }),
        getCarrelloTestataAppoggio: build.query<GenericResponse<Array<CarrelloTestataAppoggioObj>>, GenericQueryPagination>({
            providesTags: ['ordine'],
            query: (queryPagination) => ({
                url: `carrelloAppoggio/inviati/?page=${queryPagination.page}&pageSize=${queryPagination.pageSize}`,
                method: 'GET',
            }),
            transformResponse: (response: GenericResponse<any>, meta, args) => {
                return response
            },
        }),
        insertUpdateCarrelloAppoggio: build.mutation<CarrelloAppoggioResponse, CarrelloAppoggioObj>({
            invalidatesTags: ['carrello'],
            query: (data: CarrelloAppoggioObj) => ({
                url: `/carrelloAppoggio/insert`,
                method: 'PUT',
                body: data,
            }),
        }),
        duplicateCarrelloAppoggio: build.mutation<GenericDeleteResponse, CarrelloRequestDuplicate>({
            invalidatesTags: ['carrello'],
            query: (data: CarrelloRequestDuplicate) => ({
                url: `/carrelloAppoggio/duplicate`,
                method: 'PUT',
                body: data,
            }),
        }),
        insertOrdine: build.mutation<GenericInsertResponse<CarrelloConfirmResponse>, CarrelloTestataAppoggioObj>({
            invalidatesTags: ['carrello', 'searchArtAna', 'ordine'],
            query: (data: CarrelloTestataAppoggioObj) => ({
                url: `/carrelloTestataAppoggio/insertOrdine`,
                method: 'PUT',
                body: data,
            }),
        }),
        deleteRigaOrdine: build.mutation<GenericInsertResponse<number>, CarrelloAppoggioObj>({
            invalidatesTags: ['carrello'],
            query: (data: CarrelloAppoggioObj) => ({
                url: `/carrelloAppoggio`,
                method: 'DELETE',
                body: data,
            }),
        }),
        deleteOrdine: build.mutation<GenericInsertResponse<number>, void>({
            invalidatesTags: ['carrello'],
            query: () => ({
                url: `/carrelloAppoggio/ordine`,
                method: 'DELETE',
            }),
        }),
        // --------------------------------------------------------------------------------------------
        // ------------------------------------------- IMMAGINE ---------------------------------------
        // --------------------------------------------------------------------------------------------
        getListImages: build.query<ArtImageResponse, GenericListImageRequest>({
            providesTags: ['images'],
            query: (request) => ({
                url: `/imageGeneric/listImages?TIPO=${request.TIPO}&id=${request.id}`,
                method: 'GET',
            }),
            transformResponse: (response: ArtImageResponse, meta, args) => {
                response.dataImages = response.data.map((item) => {
                    return {
                        label: item.COD_TIPO_IMAGE_ART + '',
                        imgPath: `${process.env.REACT_APP_ENDPOINT}/imageGeneric/imageBin?id=${encodeURIComponent(
                            item.ART_IMAGE_ID
                        )}&TIPO=${TIPO_IMAGE.ART_ANA}&HASH=${item.HASH}`,
                    }
                })
                return response
            },
        }),
        insertImage: build.mutation<GenericInsertResponse<number>, ArtImageInsertRequest>({
            invalidatesTags: ['images'],
            query: (data: ArtImageInsertRequest) => {
                return {
                    headers: {
                        'Content-Type': 'image/jpeg',
                    },
                    url: `/imageGeneric?COD_ART=${data.COD_ART}&COD_TIPO_IMAGE_ART=${data.COD_TIPO_IMAGE_ART}&IMAGE_WIDTH=${data.IMAGE_WIDTH}&IMAGE_HEIGHT=${data.IMAGE_HEIGHT}`,
                    method: 'POST',
                    body: data.ART_IMAGE,
                }
            },
        }),
        deleteImage: build.mutation<GenericDeleteResponse, GenericDeleteRequest>({
            invalidatesTags: ['images'],
            query: (data: GenericDeleteRequest) => ({
                url: `/imageGeneric`,
                method: 'DELETE',
                body: data,
            }),
        }),
    }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetCarrelloTestataAppoggioQuery,
    useGetRicercaArtAnaQuery,
    useLazyGetRicercaArtAnaQuery,
    useGetCarrelloAppoggioQuery,
    useLazyGetCarrelloAppoggioQuery,
    useGetCategorieQuery,
    useDeleteRigaOrdineMutation,
    useDeleteOrdineMutation,
    useGetListiniOfferteQuery,
    useLazyGetListiniOfferteQuery,
    useDuplicateCarrelloAppoggioMutation,
    useInsertUpdateCarrelloAppoggioMutation,
    useGetListImagesQuery,
    useInsertImageMutation,
    useDeleteImageMutation,
    useInsertOrdineMutation,
    useLoginPostMutation,
    useLogoutPostMutation,
    useRegistrationPostMutation,
    useUpdatePassworPostMutation,
    useResetPasswordMutation,
    useGetCfQuery,
    useLazyGetCfQuery,
} = ricercaApi
